import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, SITE_URL, SITE_TITLE, SITE_DESCRIPTION, SITE_KEYWORDS, DEFAULT_OG_IMAGE, SOCIAL_LINKS } from '../../config/site';

const DEFAULT_DESCRIPTION = SITE_DESCRIPTION;
const DEFAULT_IMAGE = DEFAULT_OG_IMAGE;

const ROUTE_META = {
  '/': {
    title: 'Luxury Bags & Accessories — Modern Everyday Luxury',
    description: SITE_DESCRIPTION,
    keywords: SITE_KEYWORDS
  },
  '/collections': {
    title: 'Shop Collections',
    description: 'Browse the full curated collection of premium handbags, crossbody bags, clutches, totes, and accessories.',
    keywords: 'shop handbags, luxury bag collection, crossbody bags, totes, clutches, Modern Everyday Luxury'
  },
  '/search': {
    title: 'Search Products',
    description: 'Search the VERA by Kamakshi catalog to find handbags and accessories by name, category, or style.',
    keywords: 'search handbags, find products, luxury bags catalog'
  },
  '/cart': {
    title: 'Shopping Cart',
    description: 'Review the handbags and accessories in your cart before checkout.',
    keywords: 'shopping cart, checkout bag, cart review'
  },
  '/wishlist': {
    title: 'Wishlist',
    description: 'Save your favorite handbags and accessories for later.',
    keywords: 'wishlist, saved bags, favorite handbags'
  },
  '/about': {
    title: 'About Us',
    description: 'Learn about the story, curation, and design philosophy behind VERA by Kamakshi.',
    keywords: 'about VERA by Kamakshi, Modern Everyday Luxury, handbag brand story'
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with VERA by Kamakshi for product support, order help, and brand inquiries.',
    keywords: 'contact VERA by Kamakshi, customer support, luxury bag contact'
  },
  '/shipping-policy': {
    title: 'Shipping Policy',
    description: 'Read our shipping timelines, delivery coverage, charges, and order tracking information.',
    keywords: 'shipping policy, delivery policy, order tracking'
  },
  '/return-policy': {
    title: 'Return and Refund Policy',
    description: 'Review our return, exchange, and refund terms for VERA by Kamakshi orders.',
    keywords: 'return policy, refund policy, exchange policy'
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'Understand how VERA by Kamakshi collects, uses, and protects your personal information.',
    keywords: 'privacy policy, data protection, customer privacy'
  },
  '/terms': {
    title: 'Terms and Conditions',
    description: 'Read the terms, policies, and usage conditions for shopping with VERA by Kamakshi.',
    keywords: 'terms and conditions, shopping terms, website policy'
  },
  '/faq': {
    title: 'Frequently Asked Questions',
    description: 'Find answers to common questions about orders, shipping, returns, products, and payments.',
    keywords: 'FAQ, shipping questions, returns questions, payment help'
  },
  '/checkout': {
    title: 'Secure Checkout',
    description: 'Complete your purchase securely with delivery details and payment options.',
    keywords: 'secure checkout, pay online, order payment'
  },
  '/profile': {
    title: 'My Profile',
    description: 'Manage your profile details, saved addresses, and account information.',
    keywords: 'user profile, saved addresses, account settings'
  },
  '/orders': {
    title: 'My Orders',
    description: 'Track your orders, view purchase history, and check delivery status.',
    keywords: 'my orders, order history, order tracking'
  },
  '/admin': {
    title: 'Admin',
    description: '',
    keywords: '',
    noindex: true
  }
};

const updateMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      if (key !== 'content') {
        tag.setAttribute(key, value);
      }
    });
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', attributes.content);
};

const updateLinkTag = (selector, rel, href) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
};

const getRouteMeta = (pathname, search) => {
  // Product pages own their unique title/description in ProductDescription.
  // Only return a marker so SeoManager can still set the canonical host URL.
  if (pathname.startsWith('/product/')) {
    return {
      deferToPage: true,
      title: null,
      description: null,
      keywords: null
    };
  }

  if (pathname.startsWith('/orders/')) {
    return {
      title: 'Order Details',
      description: 'View the latest updates, items, and timeline for your order.',
      keywords: 'order details, order status, tracking'
    };
  }

  if (pathname.startsWith('/order-confirmation/')) {
    return {
      title: 'Order Confirmation',
      description: 'Your order has been placed successfully. Review the order summary and next steps.',
      keywords: 'order confirmation, purchase success, order summary'
    };
  }

  if (pathname === '/search') {
    const query = new URLSearchParams(search).get('q');
    return {
      ...ROUTE_META['/search'],
      title: query ? `Search Results for "${query}"` : ROUTE_META['/search'].title,
      description: query
        ? `Browse search results for "${query}" in the VERA by Kamakshi collection.`
        : ROUTE_META['/search'].description
    };
  }

  return ROUTE_META[pathname] || {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    keywords: 'VERA by Kamakshi, luxury handbags, accessories'
  };
};

function SeoManager() {
  const location = useLocation();

  const meta = useMemo(
    () => getRouteMeta(location.pathname, location.search),
    [location.pathname, location.search]
  );

  useEffect(() => {
    // Always use the canonical apex — never the hostname the user typed (www / web.app).
    const pathAndQuery = `${location.pathname}${location.search}`;
    const canonicalUrl = `${SITE_URL}${pathAndQuery}`;

    updateLinkTag('link[rel="canonical"]', 'canonical', canonicalUrl);
    updateMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    updateMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    const isPrivatePath =
      location.pathname.startsWith('/admin') ||
      location.pathname.startsWith('/checkout') ||
      location.pathname.startsWith('/orders') ||
      location.pathname.startsWith('/profile') ||
      location.pathname.startsWith('/cart') ||
      Boolean(meta.noindex);
    updateMetaTag('meta[name="robots"]', {
      name: 'robots',
      content: isPrivatePath
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large'
    });

    // Keep Organization sameAs in sync with configured social profiles.
    const orgScript = document.getElementById('organization-structured-data') ||
      Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
        .find((node) => {
          try {
            return JSON.parse(node.textContent || '{}')['@type'] === 'Organization';
          } catch (_) {
            return false;
          }
        });
    if (orgScript && SOCIAL_LINKS.length > 0) {
      try {
        const data = JSON.parse(orgScript.textContent || '{}');
        data.sameAs = SOCIAL_LINKS.map((link) => link.href);
        data.url = SITE_URL;
        data.logo = DEFAULT_OG_IMAGE;
        data.image = DEFAULT_OG_IMAGE;
        orgScript.textContent = JSON.stringify(data);
      } catch (_) {
        // ignore malformed existing JSON-LD
      }
    }

    // Product pages set their own unique title/description/OG image.
    if (meta.deferToPage) {
      updateMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'product' });
      return;
    }

    const pageTitle = meta.title ? `${meta.title} | ${SITE_NAME}` : SITE_TITLE;

    document.title = pageTitle;

    updateMetaTag('meta[name="description"]', { name: 'description', content: meta.description || DEFAULT_DESCRIPTION });
    updateMetaTag('meta[name="keywords"]', { name: 'keywords', content: meta.keywords || '' });

    updateMetaTag('meta[property="og:title"]', { property: 'og:title', content: pageTitle });
    updateMetaTag('meta[property="og:description"]', { property: 'og:description', content: meta.description || DEFAULT_DESCRIPTION });
    updateMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    updateMetaTag('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });

    updateMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    updateMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: pageTitle });
    updateMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description || DEFAULT_DESCRIPTION });
    updateMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });
    updateMetaTag('meta[name="twitter:url"]', { name: 'twitter:url', content: canonicalUrl });
  }, [location.pathname, location.search, meta]);

  return null;
}

export default SeoManager;